import { z } from 'zod';

export const lineItemSchema = z.object({
  position: z.string(),
  commodityGroupId: z.uuid().nullish(),
  description: z.string().optional(),
  earlyDeliveryAt: z.string().optional(),
  lateDeliveryAt: z.string().optional(),
  pricePerUnit: z.string().optional(),
  quantity: z.string().optional(),
  title: z.string().optional(),
  unitId: z.uuid().optional(),
  supplierId: z.uuid().optional(),
});
export type IntakeExtendedLineItemData = z.infer<typeof lineItemSchema>;

export const selectableLineItemSchema = lineItemSchema.extend({
  id: z.string().optional().describe('The ID used for selecting the item, will be removed upon submission'),
  position: z.string().optional(),
  currency: z.string(),
});
export type IntakeSelectableExtendedLineItemData = z.infer<typeof selectableLineItemSchema>;

export const intakeExtendedLineItemsCheckStepFormDataSchema = z
  .object({
    totalCount: z.number().optional().describe('Total count of extended line items checks'),
    noSelectionNeededCount: z
      .number()
      .optional()
      .describe('Count of line items that do not need selection because they have no matches'),
    items: z
      .record(
        z.uuid(),
        selectableLineItemSchema.optional().describe('Either line item data or a string with a reason for omission'),
      )
      .optional()
      .describe('A record mapping line item IDs to their details'),
    reasonNotSelect: z.string().optional().describe('Reason for not selecting catalogue/contract items'),
  })
  .refine(
    /**
     * Refinement to ensure that if not all line items have selections,
     *
     * @param data - The form data to validate
     * @returns boolean indicating whether the data passes the refinement
     */
    ({ noSelectionNeededCount, items, totalCount, reasonNotSelect }) => {
      const noSelectionNeededCountValue = noSelectionNeededCount ?? 0;
      const selectedItemsCount = items ? Object.keys(items).length : 0;
      const totalCountValue = totalCount ?? 0;

      // If all items do not need selection, no reason required
      if (totalCountValue === noSelectionNeededCountValue) {
        return true;
      }

      // If there are line items that do not need selection, ensure that either enough items are selected
      // or a reason is provided for not selecting items
      if (selectedItemsCount && selectedItemsCount >= totalCountValue - noSelectionNeededCountValue) {
        return true;
      }

      // Only check the reason if selection is insufficient
      return !!reasonNotSelect && reasonNotSelect.trim().length > 0;
    },
    {
      path: ['reasonNotSelect'],
      params: { i18n: 'intake:IntakeExtendedLineItemsCheckStep.validation.missingReason' },
    },
  );

export type IntakeExtendedLineItemsCheckStepFormData = z.infer<typeof intakeExtendedLineItemsCheckStepFormDataSchema>;
